import type { PlannedDayEntry } from '@functieloonhuis/shared';

interface PlanningDayCellProps {
  entry: PlannedDayEntry;
  employeeName: string;
  dimmed?: boolean;
}

export function PlanningDayCell({ entry, employeeName, dimmed }: PlanningDayCellProps) {
  // Holiday
  if (entry.isHoliday) {
    return (
      <div
        className={`flex items-center justify-center p-1.5 rounded bg-amber-50 border border-amber-200 h-10 ${dimmed ? 'opacity-40' : ''}`}
        title={entry.holidayName ?? 'Feestdag'}
        role="gridcell"
        aria-label={`${employeeName}, ${entry.holidayName ?? 'Feestdag'}`}
      >
        <span className="text-[10px] font-medium text-amber-700 truncate max-w-[80px]">
          {entry.holidayName}
        </span>
      </div>
    );
  }

  // Free / no work
  if (!entry.isWorkday) {
    return (
      <div
        className={`flex items-center justify-center p-1.5 rounded bg-gray-50 border border-gray-200 h-10 ${dimmed ? 'opacity-40' : ''}`}
        role="gridcell"
        aria-label={`${employeeName}, vrij`}
      >
        <svg className="h-4 w-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
        </svg>
      </div>
    );
  }

  // Workday
  const isKantoor = entry.location === 'kantoor';
  const bgClass = isKantoor ? 'bg-green-50' : 'bg-blue-50';
  const borderClass = isKantoor ? 'border-green-200' : 'border-blue-200';
  const iconClass = isKantoor ? 'text-green-600' : 'text-blue-600';
  const textClass = isKantoor ? 'text-green-700' : 'text-blue-700';

  const tooltipLines = [
    isKantoor ? 'Kantoor' : 'Thuis',
    entry.startTime && entry.endTime ? `${entry.startTime} - ${entry.endTime}` : null,
    entry.isHalfDay && entry.halfDayPeriod ? `Halve dag (${entry.halfDayPeriod})` : null,
  ].filter(Boolean).join('\n');

  const icon = isKantoor ? (
    <svg className={`h-3 w-3 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
    </svg>
  ) : (
    <svg className={`h-3 w-3 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );

  // Half-day: split cell — top = ochtend, bottom = middag
  if (entry.isHalfDay) {
    const period = entry.halfDayPeriod; // 'ochtend' | 'middag' | null
    return (
      <div
        className={`overflow-hidden rounded border ${borderClass} h-10 ${dimmed ? 'opacity-40' : ''}`}
        role="gridcell"
        aria-label={`${employeeName}, ${tooltipLines.replace('\n', ', ')}`}
        title={tooltipLines}
      >
        {/* Top half (ochtend) */}
        <div className={`flex items-center justify-center gap-1 h-5 ${period === 'ochtend' ? bgClass : 'bg-gray-50'}`}>
          {period === 'ochtend' && (
            <>
              {icon}
              <span className={`text-[9px] font-medium ${textClass}`}>ochtend</span>
            </>
          )}
        </div>
        {/* Bottom half (middag) */}
        <div className={`flex items-center justify-center gap-1 h-5 border-t border-dashed border-gray-200 ${period === 'middag' ? bgClass : 'bg-gray-50'}`}>
          {period === 'middag' && (
            <>
              {icon}
              <span className={`text-[9px] font-medium ${textClass}`}>middag</span>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`group relative flex items-center justify-center gap-1 p-1.5 rounded border h-10 ${bgClass} ${borderClass} ${dimmed ? 'opacity-40' : ''}`}
      role="gridcell"
      aria-label={`${employeeName}, ${tooltipLines.replace('\n', ', ')}`}
      title={tooltipLines}
    >
      {isKantoor ? (
        <svg className={`h-4 w-4 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ) : (
        <svg className={`h-4 w-4 ${iconClass}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      )}
    </div>
  );
}
